import { Injectable, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import type {
  Path,
  RecursivePartial,
  StoreState,
  TypedAction,
  UpdateSuccessPayload,
} from '@freelancer/datastore/core';
import { pluckDocumentFromRawStoreCollectionState } from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import { Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import type { CurrenciesCollection } from '../currencies';
import type { UsersSelfCollection } from './users-self.types';

@Injectable()
export class UsersSelfPrimaryCurrencyEffect {
  private actions$ = inject<Actions<TypedAction>>(Actions);
  private store$ = inject<Store<StoreState>>(Store);
  private auth = inject(Auth);

  // This allows the action to dispatch another one with populated currency values
  // when the primary currency `API_UPDATE_SUCCESS` is dispatched. This basically pulls
  // data from the `CurrenciesCollection` to replace `usersSelf.primaryCurrency` since
  // the API only returns the `currency_id` of the resource.
  readonly effect$ = createEffect(() =>
    this.actions$.pipe(
      filter(
        action =>
          action.type === 'API_UPDATE_SUCCESS' &&
          action.payload.type === 'usersSelf' &&
          // Ensure that we are filtering only initial delta, to avoid
          // infinite loop of dispatches.
          isPrimaryCurrencyInitialDelta(action.payload.delta),
      ),
      withLatestFrom(this.store$, this.auth.getUserId()),
      map(([action, state, userId]) => {
        const currencyPath: Path<CurrenciesCollection> = {
          collection: 'currencies',
          authUid: userId,
        };

        // Pull the currency data from the currencies collection
        const currency = pluckDocumentFromRawStoreCollectionState(
          state.currencies,
          currencyPath,
          action.payload.result.currency_id,
        );

        // If particular currency does not exist on the currencies collection yet,
        // let's skip dispatching the action with populated payload.
        if (!currency) {
          return undefined;
        }

        return {
          ...action,
          payload: {
            ...action.payload,
            delta: { primaryCurrency: currency },
          },
        };
      }),
      filter(isDefined),
    ),
  );
}

function isPrimaryCurrencyInitialDelta(
  delta: UpdateSuccessPayload<UsersSelfCollection>['delta'],
): delta is RecursivePartial<UsersSelfCollection['DocumentType']> {
  return (
    isDefined(delta.primaryCurrency?.id) &&
    // Having `primaryCurrency.code` means that the payload is already populated
    // with necessary data.
    !isDefined(delta.primaryCurrency?.code)
  );
}
